/**
 * Source of truth for the `Lesson::type` objects consumed throughout
 * booking system (CreateLesson.vue and the Booking* mixins).
 *
 * Historically inline at every call site, abstracted for consistency.
 *
 * @typedef {Object} LessonType
 * @property {string}  name          Display/booking name ("Reschedule", "Weekly Lesson", ...)
 * @property {string}  originalType  The underlying type being booked. Differs from
 *                                   `name` for reschedules (a rescheduled Weekly Lesson
 *                                   has name "Reschedule", originalType "Weekly Lesson").
 *
 * @property {boolean|null} recurring Seeds `form.repeating` downstream
 *                                   (BookingData `$_data_*`). Intentionally `null`
 *                                   (not `false`) for Hold/Event, matching the
 *                                   legacy catalog in SoliLessonTypes.vue.
 *
 * Only one can be true per type. Matches name field, but easier to consume
 * @property {boolean} assessment
 * @property {boolean} weeklyLesson
 * @property {boolean} reschedule
 * @property {boolean} dropIn
 * @property {boolean} hold
 * @property {boolean} event
 *
 * @property {function(Object, Object=): boolean} requireAssignment
 *                                   `(form, assignment) => boolean`. Called by
 *                                   CreateLesson.vue `submit()` and
 *                                   BookingAlerts `showRequireAssignmentAlert()`.
 *                                   The result is posted as `requireAssignment`
 *                                   and enforced by StoreLessonRequest /
 *                                   StoreGuestSessionLessonRequest / CreateLessonDto (not used yet).
 *
 * `disabled` is intentionally NOT part of the shape - SoliLessonTypes.vue is the only consumer.
 *
 * Default `requireAssignment` per type (the registry)
 * - Assessment:    false
 * - Weekly Lesson: true
 * - Reschedule:    false  (staff/calendar default — see "Context overrides")
 * - Event Reschedule: same as Event (repeating events only, with Event exceptions)
 * - Drop-In:       false
 * - Hold:          !!assignment (only when booking with an assignment)
 *                  Unique because holds are the only type that can be booked with or without an assignment
 *                  AND one-time or recurring.
 * - Event:         repeating events only, and never for registration events.
 *                  Also not needed when booking into a class (form.selectClass)
 *                  because we have the lesson exists already and we have the id
 *
 *
 * Call-site migration map (behavior-preserving)
 * ---------------------------------------------
 * - SoliLessonTypes.vue `lessonTypes`            -> makeLessonTypeCatalog()  (component still owns `disabled`)
 * - Schedule.vue handleDragDropReschedule     -> makeRescheduleType(type)
 * - LessonDetailActions.vue rescheduleLesson  -> makeRescheduleType(item.type)
 * - LessonPanels.vue rescheduleBtn            -> makeRescheduleType(item.type, {
 *                                                  requireAssignment: () => item.type !== "Event" })
 *
 * - BookingSelect.js $_select_substitutePass  -> makeRescheduleType("Event", { name: "Reschedule" })
 * - StudentBookingPanel.vue (3 buttons)       -> makeLessonType("Drop-In",   { requireAssignment: () => true })
 *                                                makeRescheduleType("Weekly Lesson", { requireAssignment: () => true })
 *                                                makeLessonType("Assessment",{ requireAssignment: () => true })
 *
 * - BookNow.vue form.type                     -> makeLessonType("Assessment")  (deferred: pending refactor)
 * - AvailabilityFinder.vue form.type          -> makeLessonType("Assessment")
 * - BookingAvailability.vue form.type         -> makeLessonType("Assessment")
 * - FoundersPrivateLessons.vue form.type      -> makeLessonType("Weekly Lesson")  (deferred: pending refactor)
 *
 *   NOTE: the last four also post a top-level `requireAssignment` boolean on
 *   their own forms; that separate payload field is untouched by this module.
 *   Their legacy type objects had NO requireAssignment method (a latent
 *   TypeError if they ever reached CreateLesson.submit()) — the factory
 *   always attaches one, closing that hole.
 */

const DEV = !window.Laravel?.production;

const IDENTITY_FLAGS = ['assessment', 'weeklyLesson', 'reschedule', 'dropIn', 'hold', 'event'];

const ALLOWED_OVERRIDES = ['name', 'originalType', 'recurring', 'requireAssignment', 'eventReschedule'];

const TYPE_DEFINITIONS = {
    Assessment: {
        flag: 'assessment',
        recurring: false,
        requireAssignment: () => false,
    },
    'Weekly Lesson': {
        flag: 'weeklyLesson',
        recurring: true,
        requireAssignment: () => true,
    },
    Reschedule: {
        flag: 'reschedule',
        // originalType is the true type being rescheduled
        originalType: 'Weekly Lesson', // default/most common
        recurring: false,
        requireAssignment: () => false,
    },
    'Drop-In': {
        flag: 'dropIn',
        recurring: false,
        requireAssignment: () => false,
    },
    Hold: {
        flag: 'hold',
        recurring: null,
        // Utilizes `requireAssignmentAlert` to ensure the assignment is recurring because holds can be one-time or recurring
        // Decided to lean on the UI error message instead of just a disabled button in case the user can't reason out why the button is disabled (one-time assignment)
        requireAssignment: (form, assignment) => !!assignment,
    },
    Event: {
        flag: 'event',
        recurring: null,
        requireAssignment: (form) => {
            const { templateEvent, address } = form;
            if (templateEvent?.registration_event) return false;
            if (form.selectClass) return false;
            if (address) return false;
            return form.repeating;
        },
    },
};

export const LESSON_TYPE_NAMES = Object.keys(TYPE_DEFINITIONS);

/**
 * Build a LessonType.
 *
 * @param {string} name One of LESSON_TYPE_NAMES.
 * @param {Object} [overrides] Optional per-call-site adjustments:
 * @param {string}  [overrides.name] Display/booking name, when it must differ
 *        from the registry key. Used by reschedules, whose registry key is
 *        "Reschedule" but whose `name` mirrors the underlying type (see
 *        makeRescheduleType). The registry key (`name` arg) still drives the
 *        identity flag + requireAssignment default.
 * @param {string}  [overrides.originalType] Underlying type (reschedules).
 * @param {boolean|null} [overrides.recurring]
 * @param {function(Object, Object=): boolean} [overrides.requireAssignment]
 *        A function returning a boolean, e.g. `() => true` or
 *        `(form) => form.someCondition`. Overrides the registry default.
 * @returns {LessonType}
 */
export function makeLessonType(name, overrides = {}) {
    const definition = TYPE_DEFINITIONS[name];

    if (DEV) {
        if (!definition) {
            console.warn(
                `[useLessonTypes] Unknown lesson type "${name}". ` +
                    `Valid names: ${LESSON_TYPE_NAMES.join(', ')}. ` +
                    `requireAssignment will default to false.`
            );
        }
        Object.keys(overrides).forEach((key) => {
            if (!ALLOWED_OVERRIDES.includes(key)) {
                console.warn(
                    `[useLessonTypes] "${name}": unknown override "${key}" ignored. ` +
                        `Allowed: ${ALLOWED_OVERRIDES.join(', ')}`
                );
            }
        });
        if (overrides.requireAssignment != null && typeof overrides.requireAssignment !== 'function') {
            console.warn(
                `[useLessonTypes] "${name}": requireAssignment override must be a function (form, assignment) => boolean, got ${typeof overrides.requireAssignment}.`
            );
        }
    }

    // Recurring is a special case because it can be null (Hold/Event)
    // null form values are treated as "not set" and will be presented to the user for selection
    let recurring = false;
    if (definition.recurring !== undefined) {
        recurring = definition.recurring;
    }
    if (overrides?.recurring !== undefined) {
        recurring = overrides.recurring;
    }

    const type = {
        name: overrides.name ?? name,
        originalType: overrides.originalType ?? definition?.originalType ?? name,
        recurring: recurring,
        requireAssignment: overrides.requireAssignment ?? definition?.requireAssignment ?? (() => false),
        eventReschedule: overrides.eventReschedule ?? false,
    };

    // Always materialize every identity flag as a real boolean so consumers
    // like `form.type?.hold` / `event.type?.reschedule` never see undefined.
    IDENTITY_FLAGS.forEach((flag) => {
        type[flag] = definition?.flag === flag;
    });

    return type;
}

/**
 * Weekly Lesson reschedules are just called "Reschedule".
 * And thus we can also assume that a reschedule is always a Weekly Lesson, so we swap the name and originalType.
 * TODO: `Lesson::is_reschedule` can replace this logic, but requires refactoring of critical logic.
 *
 * @param {string} fromTypeName The existing lesson's type name
 *        (e.g. `lesson.type` / `arg.event.extendedProps.type`).
 * @param {Object} [overrides] Same options as makeLessonType. Most self-serve
 *        callers will pass a `requireAssignment` override here.
 * @returns {LessonType}
 */
export function makeRescheduleType(fromTypeName, overrides = {}) {
    return makeLessonType('Reschedule', {
        name: fromTypeName === 'Weekly Lesson' ? 'Reschedule' : fromTypeName,
        originalType: fromTypeName === 'Reschedule' ? 'Weekly Lesson' : fromTypeName,
        ...overrides,
    });
}

export const isEventReschedule = (type) => Boolean(type?.reschedule && type?.eventReschedule);

/**
 * Full catalog for SoliLessonTypes.vue's `lessonTypes`.

 * @returns {{assessment: LessonType, weeklyLesson: LessonType,
 *            reschedule: LessonType, dropIn: LessonType,
 *            hold: LessonType, event: LessonType}}
 */
export function makeLessonTypeCatalog() {
    return {
        assessment: makeLessonType('Assessment'),
        weeklyLesson: makeLessonType('Weekly Lesson'),
        reschedule: makeLessonType('Reschedule'),
        dropIn: makeLessonType('Drop-In'),
        hold: makeLessonType('Hold'),
        event: makeLessonType('Event'),
    };
}

export function useLessonTypes() {
    return {
        makeLessonType,
        makeRescheduleType,
        makeLessonTypeCatalog,
        LESSON_TYPE_NAMES,
        isEventReschedule,
    };
}
